import React, { useCallback, useEffect, useRef } from 'react'

import { Loading } from '@fourthwall/components'

import { useDashboardFeatureFlags } from '../../../../components/utils/DashboardFeatureFlags'
import { useMobileCookies } from '../../hooks/useMobileCookies'
import { useThemeEditorContext } from '../../hooks/useThemeEditorContext'
import { InspectorFrame } from '../../modules/LivePreview/modules/InspectorFrame/InspectorFrame'
import { useThemeLivePreviewContext } from '../../modules/LivePreview/providers/ThemeLivePreviewProvider'
import type { PreviewEvents } from '../../modules/LivePreview/types'
import * as S from './ThemePreview.styled'
import type { ThemePreviewProps } from './types'

export const ThemePreview = ({
  iframeTimestamp,
  shopUrl,
  previewMode = 'desktop',
  previewSectionKey,
  previewInspector,
  className,
}: ThemePreviewProps) => {
  const { isPreviewLoading, setIsPreviewLoading } = useThemeEditorContext()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { channel, setInspectorVisible } = useThemeLivePreviewContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  useMobileCookies([iframeTimestamp, isPreviewLoading])

  useEffect(() => {
    setIsPreviewLoading(true)
  }, [iframeTimestamp, shopUrl])

  const scrollToPreviewSection = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      try {
        if (!iframeRef.current || !iframeRef.current.contentWindow) return

        const elementId = `fw-section-${previewSectionKey}`
        const element = iframeRef.current.contentWindow.document.getElementById(elementId)

        element && element.scrollIntoView({ behavior })
      } catch {}
    },
    [previewSectionKey],
  )

  useEffect(() => {
    if (previewSectionKey) {
      scrollToPreviewSection()
    }
  }, [previewSectionKey, scrollToPreviewSection])

  const handleLoad = () => {
    if (previewSectionKey) {
      scrollToPreviewSection('auto')
    }

    setIsPreviewLoading(false)
  }

  useEffect(() => {
    const handleEvent = (event: MessageEvent<PreviewEvents>) => {
      if (event.data.action === 'ThemePreview:CurrentPage') {
        handleLoad()
      }
    }

    if (dashboardFeatureFlags.themeEditorFastPreview.enabled) {
      channel.on(handleEvent)
    }
  }, [channel, previewSectionKey])

  return (
    <S.Container className={className}>
      <S.Wrapper
        $previewMode={previewMode}
        onMouseEnter={() => setInspectorVisible(true)}
        onMouseLeave={() => setInspectorVisible(false)}
      >
        <Loading isLoading={isPreviewLoading} variant="solid">
          {previewInspector && <InspectorFrame />}
          <iframe
            key={iframeTimestamp}
            ref={iframeRef}
            src={shopUrl}
            title="Shop preview"
            width="100%"
            height="100%"
            style={{
              border: 'none',
            }}
            onLoad={handleLoad}
          />
        </Loading>
      </S.Wrapper>
    </S.Container>
  )
}
